import { useEffect, useState } from 'react';
import axios from 'axios';
import { Key, Plus, Trash2, Edit, Eye, EyeOff, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function APIKeys() {
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);
  const [showKeyValue, setShowKeyValue] = useState({});
  const [copiedId, setCopiedId] = useState(null);
  
  const [formData, setFormData] = useState({
    service_name: '',
    key_name: '',
    key_value: '',
    description: '',
    category: 'other'
  });

  useEffect(() => {
    fetchAPIKeys();
  }, []);

  const fetchAPIKeys = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/api-keys`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setApiKeys(response.data);
    } catch (error) {
      console.error('Error fetching API keys:', error);
      toast.error('Failed to fetch API keys');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateKey = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API}/api-keys`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('API key created successfully');
      setShowDialog(false);
      setFormData({
        service_name: '',
        key_name: '',
        key_value: '',
        description: '',
        category: 'other'
      });
      fetchAPIKeys();
    } catch (error) {
      console.error('Error creating API key:', error);
      toast.error(error.response?.data?.detail || 'Failed to create API key');
    }
  };

  const handleDeleteKey = async (keyId) => {
    if (!window.confirm('Are you sure you want to delete this API key?')) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/api-keys/${keyId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('API key deleted successfully');
      fetchAPIKeys();
    } catch (error) {
      console.error('Error deleting API key:', error);
      toast.error('Failed to delete API key');
    }
  };

  const handleViewKey = async (keyId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/api-keys/${keyId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSelectedKey(response.data);
      setShowViewDialog(true);
    } catch (error) {
      console.error('Error fetching API key details:', error);
      toast.error('Failed to fetch API key details');
    }
  };

  const toggleKeyVisibility = (keyId) => {
    setShowKeyValue(prev => ({ ...prev, [keyId]: !prev[keyId] }));
  };

  const copyToClipboard = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopiedId(null), 2000);
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  const getCategoryBadge = (category) => {
    const colors = {
      image: 'bg-purple-100 text-purple-800',
      video: 'bg-blue-100 text-blue-800',
      audio: 'bg-green-100 text-green-800',
      other: 'bg-gray-100 text-gray-800',
    };
    return colors[category] || colors.other;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-lg text-slate-600">Loading API keys...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold text-slate-800 mb-2">API Keys Management</h1>
          <p className="text-slate-600">Securely store and manage API keys for external services</p>
        </div>
        <Button
          onClick={() => setShowDialog(true)}
          className="bg-purple-600 hover:bg-purple-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add API Key
        </Button>
      </div>

      {/* API Keys List */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Service</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Key Name</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Category</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Status</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Usage</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {apiKeys.length === 0 ? (
              <tr>
                <td colSpan="6" className="px-6 py-8 text-center text-slate-500">
                  No API keys found. Click "Add API Key" to create one.
                </td>
              </tr>
            ) : (
              apiKeys.map((key) => (
                <tr key={key.id} className="hover:bg-slate-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <Key className="w-5 h-5 text-slate-400 mr-3" />
                      <div>
                        <div className="font-medium text-slate-900">{key.service_name}</div>
                        <div className="text-sm text-slate-500">{key.description}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <code className="text-sm bg-slate-100 px-2 py-1 rounded">
                      {key.key_name}
                    </code>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getCategoryBadge(key.category)}`}>
                      {key.category}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      key.is_active 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {key.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600">
                    {key.usage_count} times
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleViewKey(key.id)}
                        className="text-blue-600 hover:text-blue-800"
                        title="View Key"
                      >
                        <Eye className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDeleteKey(key.id)}
                        className="text-red-600 hover:text-red-800"
                        title="Delete Key"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Add New API Key</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateKey}>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="service_name">Service Name</Label>
                <Input
                  id="service_name"
                  placeholder="e.g., Google Gemini AI"
                  value={formData.service_name}
                  onChange={(e) => setFormData({...formData, service_name: e.target.value})}
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="key_name">Key Name</Label>
                <Input
                  id="key_name"
                  placeholder="e.g., GEMINI_API_KEY"
                  value={formData.key_name}
                  onChange={(e) => setFormData({...formData, key_name: e.target.value})}
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="key_value">API Key Value</Label>
                <Input
                  id="key_value"
                  type="password"
                  placeholder="Enter the actual API key"
                  value={formData.key_value}
                  onChange={(e) => setFormData({...formData, key_value: e.target.value})}
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="category">Category</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({...formData, category: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="image">Image</SelectItem>
                    <SelectItem value="video">Video</SelectItem>
                    <SelectItem value="audio">Audio</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="description">Description</Label>
                <Input
                  id="description"
                  placeholder="Optional description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-purple-600 hover:bg-purple-700">
                Create API Key
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Key Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>API Key Details</DialogTitle>
          </DialogHeader>
          {selectedKey && (
            <div className="space-y-4 py-4">
              <div>
                <Label>Service Name</Label>
                <p className="text-sm text-slate-900 mt-1">{selectedKey.service_name}</p>
              </div>
              
              <div>
                <Label>Key Name</Label>
                <p className="text-sm text-slate-900 mt-1">
                  <code className="bg-slate-100 px-2 py-1 rounded">{selectedKey.key_name}</code>
                </p>
              </div>
              
              <div>
                <Label>API Key Value</Label>
                <div className="flex items-center space-x-2 mt-1">
                  <code className="flex-1 text-sm bg-slate-100 px-3 py-2 rounded break-all">
                    {showKeyValue[selectedKey.id] ? selectedKey.key_value : '••••••••••••••••••••••••'}
                  </code>
                  <button
                    onClick={() => toggleKeyVisibility(selectedKey.id)}
                    className="text-slate-600 hover:text-slate-800"
                  >
                    {showKeyValue[selectedKey.id] ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                  <button
                    onClick={() => copyToClipboard(selectedKey.key_value, selectedKey.id)}
                    className="text-slate-600 hover:text-slate-800"
                  >
                    {copiedId === selectedKey.id ? <Check className="w-5 h-5 text-green-600" /> : <Copy className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              
              <div>
                <Label>Category</Label>
                <p className="text-sm text-slate-900 mt-1">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getCategoryBadge(selectedKey.category)}`}>
                    {selectedKey.category}
                  </span>
                </p>
              </div>
              
              <div>
                <Label>Description</Label>
                <p className="text-sm text-slate-600 mt-1">{selectedKey.description || 'No description'}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Created At</Label>
                  <p className="text-sm text-slate-600 mt-1">
                    {new Date(selectedKey.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <Label>Usage Count</Label>
                  <p className="text-sm text-slate-600 mt-1">{selectedKey.usage_count} times</p>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button onClick={() => setShowViewDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Info Box */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <Key className="w-5 h-5 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-blue-900 mb-1">Security Information</h3>
            <p className="text-sm text-blue-800">
              All API keys are encrypted at rest using AES-256 encryption. Keys can be accessed by your external applications using the dedicated API endpoint with proper authentication.
            </p>
            <div className="mt-3 text-sm">
              <strong className="text-blue-900">External Access Endpoint:</strong>
              <code className="ml-2 bg-blue-100 px-2 py-1 rounded text-blue-900">
                GET {BACKEND_URL}/api/external/keys
              </code>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
